import {
    Box,
    Button,
    Center,
    Divider,
    Flex,
    HStack,
    LightMode,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
    useColorModeValue,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import React from "react";
import {BsArrowLeft as BackToMainIcon} from "react-icons/bs";
import {IoIosCheckmarkCircle as SuccessIcon} from "react-icons/io";

import {PRICING_SCHEMA_TYPES, SEATING_MODE} from "../../../constants";
import useDraftShoppingCart from "../../hooks/useDraftShoppingCart";
import useShoppingCart from "../../hooks/useShoppingCart";
import CheckoutItem from "../CheckoutItem/CheckoutItem";
import CheckoutPriceValues from "../CheckoutPriceValues/CheckoutPriceValues";
import PaymentForm from "../PaymentForm/PaymentForm";

const formatToShoppingCartItemShape = ({
                                           featureId,
                                           pricingSchemaType = PRICING_SCHEMA_TYPES.NON_REFUNDABLE,
                                           cartItemName,
                                           start,
                                           end,
                                           cartItemQuantity,
                                           categoryItemMaxOccupancy,
                                           seatingMode,
                                       }) => {
    let quantity = 1;
    let occupancy = 1
    if (seatingMode === SEATING_MODE.PER_SEAT) {
        quantity = cartItemQuantity
        occupancy = cartItemQuantity
    } else {
        quantity = cartItemQuantity
        occupancy = categoryItemMaxOccupancy * cartItemQuantity
    }
    return {
        "start": start,
        "end": end,
        "pricing_schema_type": pricingSchemaType,
        "pricing_schema_name": cartItemName,
        "occupancy": occupancy,
        "quantity": quantity,
        "feature": featureId
    }
}


function CheckoutModal(props) {
    const toast = useToast()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {
        isLoading,
        isPaymentSuccessful,
        createNewCart,
        isDraftShoppingCartUpdated,
        shoppingCartId
    } = useShoppingCart()
    const {draftShoppingCartItems, isCartEmpty} = useDraftShoppingCart()
    const HeaderBG = useColorModeValue('#E8ECED', '#2F2F2F')
    const ModalBG =  useColorModeValue('#F9FAFA', '#3D3D3D')
    
    const syncShoppingCartAndOpenCheckout = () => isDraftShoppingCartUpdated || !shoppingCartId ? createNewCart(draftShoppingCartItems.map((item) => formatToShoppingCartItemShape(item))).then(onOpen).catch((error) => {
        // TODO: This error handling can very well be extracted into a separate hook of itself
        if (typeof error === 'string') {
            toast({
                title: 'Uh Oh!',
                description: error,
                status: 'error',
                duration: 9000,
                isClosable: true
            })
        } else if (Array.isArray(error)) {
            toast({
                title: 'Uh Oh!',
                description: error.join('\n'),
                status: 'error',
                duration: 9000,
                isClosable: true
            })
        } else if (error && typeof error === 'object') {
            toast({
                title: 'Uh Oh!',
                description: JSON.stringify(error, null, 2),
                status: 'error',
                duration: 9000,
                isClosable: true
            })
        }
    }) : onOpen()

    return (
        <>
            <LightMode>
                <Button
                    isLoading={isLoading}
                    isDisabled={isCartEmpty}
                    isFullWidth
                    onClick={syncShoppingCartAndOpenCheckout}
                    size="lg"
                    colorScheme="orange"
                    variant="solid"
                >
                    Checkout
                </Button>
            </LightMode>

            <Modal isCentered={isPaymentSuccessful} size="xl" isOpen={isOpen} onClose={onClose}
                   motionPreset="slideInBottom"
                   autoFocus={false}
                   closeOnOverlayClick={false}>
                <ModalOverlay bg='rgba(0, 0, 0, 0.9)'/>
                {isPaymentSuccessful && (
                    <ModalContent>
                        <ModalHeader bg={HeaderBG} fontWeight='bold' borderTopLeftRadius='8px'
                                     borderTopRightRadius='8px'>
                            <Flex justify='center' align='center'>
                                <Box display='inline-flex' pr={2}> <SuccessIcon size='20px'/> </Box>
                                <Box>Order placed!</Box>
                            </Flex>
                        </ModalHeader>
                        {/* <ModalCloseButton /> */}
                        <ModalBody bg={ModalBG}>
                            <HStack pb={2} justify={{base: 'center', sm: 'flex-start'}} fontSize='md' fontWeight="bold">
                                <Text> Your order is finalized and details are sent to your email. </Text>
                            </HStack>
                            <Flex direction='row'>
                                <Box width='100%' pb={4}>
                                    <Center>
                                        <Button isFullWidth onClick={onClose} mt={4}>
                                            Confirm & Close
                                        </Button>
                                    </Center>
                                </Box>
                            </Flex>
                        </ModalBody>
                    </ModalContent>
                )}
                {!isPaymentSuccessful && (
                    <ModalContent>
                        <ModalHeader bg={HeaderBG} fontWeight='bold' borderTopLeftRadius='8px'
                                     borderTopRightRadius='8px'>
                            Complete your order
                        </ModalHeader>
                        {/* <ModalCloseButton /> */}
                        <ModalBody bg={ModalBG}>
                            {/* <HStack pb={2} justify={{base: 'center', sm: 'flex-end'}} fontSize='md' fontWeight="bold">
                                <Text> Your tickets are reserved for </Text>
                                <Stack direction='row' spacing={1} justify='flex-end' align='center' color='red'>
                                    <StopwatchIcon size='14px'/>
                                    <Text>9:00</Text>
                                </Stack>
                            </HStack> */}
                            <Flex direction='row'>
                                <Box width={4}>
                                    <Text fontSize='lg' fontWeight="bold">1.</Text>
                                </Box>
                                <Box width='100%'>
                                    <Text fontSize='lg' fontWeight="bold"> Review Items </Text>
                                    {/* TODO: Use actual shopping cart items to display this */}
                                    {draftShoppingCartItems.map((item, index) => (
                                        <CheckoutItem
                                            cartItem={item}
                                            key={`${item.categoryItemName}-${item.categoryItemPrice}`}
                                        />
                                    ))}
                                    <CheckoutPriceValues/>
                                    <Center>
                                        <Button leftIcon={<BackToMainIcon/>} onClick={onClose} variant='ghost'>
                                            Modify your order
                                        </Button>
                                    </Center>
                                </Box>
                            </Flex>

                            <Box pt={2} pb={2}>
                                <Divider variant='hidden' opacity='1'/>
                            </Box>
                            <PaymentForm/>
                        </ModalBody>
                    </ModalContent>
                )}
            </Modal>

        </>
    )
}

export default CheckoutModal